import React, { useEffect, useState, useMemo } from "react";
import Chart from "react-apexcharts";
import { useRoleVerify } from "Common/RoleVerify";

import { GetDashboardData } from "adminApi/DashboardApi/Dashboard";
import BookingTable from "./Components/BookingTable";

import {
  Users,
  CalendarCheck,
  DollarSign,
  TrendingUp,
  ArrowUpRight,
  Hotel,
  AlertTriangle,
} from "lucide-react";

// ─────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
  }).format(Number(value) || 0);

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN").format(Number(value) || 0);

// ─────────────────────────────────────────────────────
// Dashboard Component
// ─────────────────────────────────────────────────────

const Index = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [data, setData] = useState({
    cards: null,
    chart: null,
    inventory: null,
  });

  // ───────────────────────────────────────────────────
  // Permission
  // ───────────────────────────────────────────────────

  const canShowAction = useRoleVerify(
    "dashbord-totle-show",
    ["can_access"]
  );

  // ───────────────────────────────────────────────────
  // Random / Masked Data
  //
  // Used only when user does NOT have permission
  // to see revenue information.
  // ───────────────────────────────────────────────────

  const maskedDashboardData = useMemo(() => {
    const randomNumber = (min, max) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    const bookingActivity = Array.from({ length: 12 }, () =>
      randomNumber(0, 5)
    );

    const generalActivity = Array.from({ length: 12 }, () =>
      randomNumber(10, 100)
    );

    return {
      cards: {
        todayActivity: randomNumber(10, 50),
        totalActivity: randomNumber(100, 500),
      },

      chart: {
        labels: [
          "14 Aug",
          "15 Aug",
          "16 Aug",
          "17 Aug",
          "18 Aug",
          "19 Aug",
          "20 Aug",
          "21 Aug",
          "22 Aug",
          "23 Aug",
          "24 Aug",
          "25 Aug",
        ],

        bookings: bookingActivity,
        activity: generalActivity,
      },
    };
  }, []);

  // ───────────────────────────────────────────────────
  // Fetch Dashboard Data
  // ───────────────────────────────────────────────────

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await GetDashboardData(controller.signal);

        if (result?.success) {
          setData(result.data);
        } else {
          setError(
            result?.message || "Failed to load dashboard data."
          );
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Dashboard Fetch Error:", err);
          setError(
            "Could not reach the server. Please try again."
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => controller.abort();
  }, []);

  // ───────────────────────────────────────────────────
  // Card Config
  // ───────────────────────────────────────────────────

  const cardData = useMemo(() => {
    const cards = data.cards || {
      todayBooking: 0,
      todayRevenue: 0,
      totalCustomer: 0,
      totalRevenue: 0,
    };

    // ─────────────────────────────────────────────────
    // Permission TRUE
    //
    // Show actual revenue information
    // ─────────────────────────────────────────────────

    if (canShowAction) {
      return [
        {
          title: "Today's Bookings",
          value: formatNumber(cards.todayBooking),
          trend: "Real-time",
          icon: (
            <CalendarCheck
              size={24}
              className="text-indigo"
            />
          ),
          color: "indigo",
          bg: "rgba(99, 102, 241, 0.15)",
          borderColor: "#6366f1",
        },

        {
          title: "Today's Revenue",
          value: formatCurrency(cards.todayRevenue),
          trend: "Today",
          icon: (
            <DollarSign
              size={24}
              className="text-emerald"
            />
          ),
          color: "emerald",
          bg: "rgba(16, 185, 129, 0.15)",
          borderColor: "#10b981",
        },

        {
          title: "Total Customer",
          value: formatNumber(cards.totalCustomer),
          trend: "Active",
          icon: (
            <Users
              size={24}
              className="text-amber"
            />
          ),
          color: "amber",
          bg: "rgba(245, 158, 11, 0.15)",
          borderColor: "#f59e0b",
        },

        {
          title: "Total Revenue",
          value: formatCurrency(cards.totalRevenue),
          trend: "Life-time",
          icon: (
            <TrendingUp
              size={24}
              className="text-rose"
            />
          ),
          color: "rose",
          bg: "rgba(244, 63, 94, 0.15)",
          borderColor: "#f43f5e",
        },
      ];
    }

    // ─────────────────────────────────────────────────
    // Permission FALSE
    //
    // Do NOT show revenue.
    // Keep exactly 4 cards to preserve design.
    // ─────────────────────────────────────────────────

    return [
      {
        title: "Today's Bookings",
        value: formatNumber(cards.todayBooking),
        trend: "Real-time",
        icon: (
          <CalendarCheck
            size={24}
            className="text-indigo"
          />
        ),
        color: "indigo",
        bg: "rgba(99, 102, 241, 0.15)",
        borderColor: "#6366f1",
      },

      {
        title: "Today's Activity",
        value: formatNumber(
          maskedDashboardData.cards.todayActivity
        ),
        trend: "Today",
        icon: (
          <CalendarCheck
            size={24}
            className="text-emerald"
          />
        ),
        color: "emerald",
        bg: "rgba(16, 185, 129, 0.15)",
        borderColor: "#10b981",
      },

      {
        title: "Total Customer",
        value: formatNumber(cards.totalCustomer),
        trend: "Active",
        icon: (
          <Users
            size={24}
            className="text-amber"
          />
        ),
        color: "amber",
        bg: "rgba(245, 158, 11, 0.15)",
        borderColor: "#f59e0b",
      },

      {
        title: "Total Activity",
        value: formatNumber(
          maskedDashboardData.cards.totalActivity
        ),
        trend: "Overview",
        icon: (
          <TrendingUp
            size={24}
            className="text-rose"
          />
        ),
        color: "rose",
        bg: "rgba(244, 63, 94, 0.15)",
        borderColor: "#f43f5e",
      },
    ];
  }, [
    data.cards,
    canShowAction,
    maskedDashboardData,
  ]);

  // ───────────────────────────────────────────────────
  // Chart Config
  // ───────────────────────────────────────────────────

  const chartOptions = useMemo(() => {
    const labels = canShowAction
      ? data.chart?.labels || []
      : maskedDashboardData.chart.labels;

    return {
      chart: {
        height: 320,
        type: "line",
        toolbar: {
          show: false,
        },
        fontFamily: "'Inter', sans-serif",
        animations: {
          enabled: false,
        },
        zoom: {
          enabled: false,
        },
      },

      colors: ["#6366f1", "#10b981"],

      stroke: {
        width: [0, 2],
        curve: "smooth",
      },

      plotOptions: {
        bar: {
          columnWidth: "40%",
          borderRadius: 4,
        },
      },

      fill: {
        type: ["solid", "gradient"],

        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.5,
          opacityFrom: 0.4,
          opacityTo: 0,
          stops: [0, 90, 100],
        },
      },

      labels,

      xaxis: {
        type: "category",

        labels: {
          style: {
            colors: "#64748b",
            fontSize: "12px",
          },
        },
      },

      yaxis: [
        {
          title: {
            text: "Bookings",
            style: {
              color: "#6366f1",
              fontWeight: 600,
            },
          },

          labels: {
            style: {
              colors: "#64748b",
            },

            formatter: (val) =>
              Number(val).toFixed(0),
          },
        },

        {
          opposite: true,

          title: {
            text: canShowAction
              ? "Revenue"
              : "Activity",

            style: {
              color: "#10b981",
              fontWeight: 600,
            },
          },

          labels: {
            style: {
              colors: "#64748b",
            },

            formatter: (val) =>
              canShowAction
                ? formatCurrency(val)
                : Number(val).toFixed(0),
          },
        },
      ],

      tooltip: {
        shared: true,
        intersect: false,
        theme: "light",
      },

      legend: {
        position: "top",
        horizontalAlign: "right",
      },

      grid: {
        borderColor: "#e2e8f0",
        strokeDashArray: 4,
      },
    };
  }, [
    data.chart,
    canShowAction,
    maskedDashboardData,
  ]);

  // ───────────────────────────────────────────────────
  // Chart Series
  // ───────────────────────────────────────────────────

  const chartSeries = useMemo(() => {
    // ─────────────────────────────────────────────────
    // Permission TRUE
    //
    // Actual revenue chart
    // ─────────────────────────────────────────────────

    if (canShowAction) {
      const chartData = data.chart || {
        bookings: [],
        revenue: [],
      };

      return [
        {
          name: "Bookings Volume",
          type: "column",
          data: chartData.bookings,
        },

        {
          name: "Daily Revenue (₹)",
          type: "area",
          data: chartData.revenue,
        },
      ];
    }

    // ─────────────────────────────────────────────────
    // Permission FALSE
    //
    // No revenue data shown.
    // Use random activity values instead.
    // ─────────────────────────────────────────────────

    return [
      {
        name: "Booking Activity",
        type: "column",
        data: maskedDashboardData.chart.bookings,
      },

      {
        name: "Activity Trend",
        type: "area",
        data: maskedDashboardData.chart.activity,
      },
    ];
  }, [
    data.chart,
    canShowAction,
    maskedDashboardData,
  ]);

  // ───────────────────────────────────────────────────
  // Inventory
  // Available vs Booked
  // ───────────────────────────────────────────────────

  const occupancyPct = useMemo(() => {
    const total = data.inventory?.total || 0;
    const booked = data.inventory?.booked || 0;
    const notReserve =
      data.inventory?.notReserve || 0;

    const sellableRooms =
      total - notReserve;

    return sellableRooms > 0
      ? ((booked / sellableRooms) * 100).toFixed(1)
      : 0;
  }, [data.inventory]);

  // ───────────────────────────────────────────────────
  // Loading Skeleton
  // ───────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="px-xl-5 px-lg-4 px-3 py-4 page-body">
        <div className="row g-3 mb-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              className="col-md-6 col-lg-3"
              key={i}
            >
              <div
                className="card border-0 shadow-sm"
                style={{
                  borderRadius: "16px",
                }}
              >
                <div className="card-body p-4 placeholder-glow">
                  <div
                    className="placeholder col-4 mb-3"
                    style={{
                      height: "40px",
                      borderRadius: "12px",
                    }}
                  ></div>

                  <div
                    className="placeholder col-8 mb-2"
                    style={{
                      height: "24px",
                    }}
                  ></div>

                  <div
                    className="placeholder col-6"
                    style={{
                      height: "16px",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="card border-0 shadow-sm"
          style={{
            borderRadius: "16px",
          }}
        >
          <div
            className="card-body p-5 d-flex align-items-center justify-content-center"
            style={{
              height: 400,
            }}
          >
            <div
              className="spinner-border text-primary"
              role="status"
            ></div>
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────
  // Error State
  // ───────────────────────────────────────────────────

  if (error) {
    return (
      <div className="px-xl-5 px-lg-4 px-3 py-4 page-body">
        <div
          className="card border-0 shadow-sm"
          style={{
            borderRadius: "16px",
          }}
        >
          <div className="card-body p-5 text-center">
            <AlertTriangle
              size={32}
              className="text-danger mb-3"
            />

            <h6 className="fw-bold mb-1">
              Couldn't load dashboard
            </h6>

            <p className="text-muted small mb-0">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ───────────────────────────────────────────────────
  // Main Render
  // ───────────────────────────────────────────────────

  return (
    <div className="px-xl-5 px-lg-4 px-3 py-4 page-body bg-light-subtle">

      {/* ─────────────────────────────────────────────
          Header
      ───────────────────────────────────────────── */}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">
            Performance Dashboard
          </h4>

          <p className="text-muted small mb-0">
            Live operational data from the hotel database.
          </p>
        </div>
      </div>

      {/* ─────────────────────────────────────────────
          Cards
      ───────────────────────────────────────────── */}

      <div className="row g-3 mb-4">
        {cardData.map((card, index) => (
          <div
            className="col-md-6 col-lg-3"
            key={index}
          >
            <div
              className="card border-0 shadow-sm h-100 overflow-hidden"
              style={{
                borderRadius: "16px",
              }}
            >
              <div className="card-body p-4">

                <div className="d-flex justify-content-between align-items-start mb-3">

                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: "48px",
                      height: "48px",
                      backgroundColor: card.bg,
                    }}
                  >
                    {card.icon}
                  </div>

                  <div
                    className={`badge bg-${card.color}-subtle text-${card.color} border-0 rounded-pill px-2 py-1 small d-flex align-items-center gap-1`}
                  >
                    <ArrowUpRight size={12} />

                    {card.trend}
                  </div>
                </div>

                <h6 className="text-muted fw-medium mb-1 small uppercase tracking-wider">
                  {card.title}
                </h6>

                <div className="d-flex align-items-baseline gap-2">
                  <h3 className="fw-bold mb-0">
                    {card.value}
                  </h3>
                </div>

              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ─────────────────────────────────────────────
          Main Dashboard Row
      ───────────────────────────────────────────── */}

      <div className="row g-3">

        {/* ───────────────────────────────────────────
            Chart
        ─────────────────────────────────────────── */}

        <div className="col-lg-7">
          <div
            className="card border-0 shadow-sm h-100"
            style={{
              borderRadius: "16px",
            }}
          >

            <div className="card-header bg-transparent border-0 p-4 pb-0">

              <h6 className="card-title fw-bold mb-1">
                {canShowAction
                  ? "Bookings & Revenue Trend"
                  : "Booking Activity Trend"}
              </h6>

              <p className="text-muted small mb-0">
                {canShowAction
                  ? "Daily business cycle for the last 12 days"
                  : "Daily operational activity for the last 12 days"}
              </p>

            </div>

            <div className="card-body p-4">

              <Chart
                options={chartOptions}
                series={chartSeries}
                height={320}
                type="line"
              />

            </div>
          </div>
        </div>

        {/* ───────────────────────────────────────────
            Inventory Status
        ─────────────────────────────────────────── */}

        <div className="col-lg-5">
          <div
            className="card border-0 shadow-sm h-100"
            style={{
              borderRadius: "16px",
            }}
          >

            <div className="card-header bg-transparent border-0 p-4 pb-0">

              <h6 className="card-title fw-bold mb-1">
                Inventory Status
              </h6>

              <p className="text-muted small mb-0">
                Current room availability
              </p>

            </div>

            <div className="card-body p-4">

              <div className="d-flex flex-column gap-3">

                {/* Total Units */}

                <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 transition-hover">

                  <div className="d-flex align-items-center gap-3">

                    <div className="bg-primary-subtle p-2 rounded-3 text-primary">
                      <Hotel size={20} />
                    </div>

                    <div>
                      <p className="mb-0 small fw-medium">
                        Total Units
                      </p>

                      <h6 className="mb-0 fw-bold">
                        {data.inventory?.total || 0} Rooms
                      </h6>
                    </div>

                  </div>

                </div>

                {/* Available / Booked / Not Reserve */}

                <div className="row g-2 mb-2">

                  {/* Available */}

                  <div className="col-4">

                    <div className="p-2 border rounded-3 text-center bg-success-subtle border-success-subtle">

                      <p className="small text-success fw-bold mb-0">
                        Available
                      </p>

                      <h5 className="mb-0 fw-bold text-success">
                        {data.inventory?.available || 0}
                      </h5>

                    </div>

                  </div>

                  {/* Booked */}

                  <div className="col-4">

                    <div className="p-2 border rounded-3 text-center bg-danger-subtle border-danger-subtle">

                      <p className="small text-danger fw-bold mb-0">
                        Booked
                      </p>

                      <h5 className="mb-0 fw-bold text-danger">
                        {data.inventory?.booked || 0}
                      </h5>

                    </div>

                  </div>

                  {/* Not Reserve */}

                  <div className="col-4">

                    <div className="p-2 border rounded-3 text-center bg-warning-subtle border-warning-subtle">

                      <p className="small text-warning fw-bold mb-0">
                        Not Reserve
                      </p>

                      <h5 className="mb-0 fw-bold text-warning">
                        {data.inventory?.notReserve || 0}
                      </h5>

                    </div>

                  </div>

                </div>

                {/* Occupancy */}

                <div className="p-3 bg-primary-subtle rounded-3 border border-primary-subtle">

                  <div className="d-flex justify-content-between mb-2">

                    <p className="small text-primary fw-bold mb-0">
                      Occupancy Rate
                    </p>

                    <p className="small text-primary fw-bold mb-0">
                      {occupancyPct}%
                    </p>

                  </div>

                  <div
                    className="progress overflow-visible"
                    style={{
                      height: "8px",
                      backgroundColor:
                        "rgba(var(--primary-rgb), 0.2)",
                    }}
                  >

                    <div
                      className="progress-bar bg-primary rounded-pill shadow-sm"
                      style={{
                        width: `${occupancyPct}%`,
                      }}
                    ></div>

                  </div>

                </div>

              </div>

            </div>
          </div>
        </div>

        {/* ───────────────────────────────────────────
            Arrival List Table
        ─────────────────────────────────────────── */}

        <div className="col-12 mt-3">

          <div
            className="card border-0 shadow-sm"
            style={{
              borderRadius: "16px",
            }}
          >

            <div className="card-header bg-transparent border-0 p-4 pb-0">

              <h6 className="card-title fw-bold mb-1">
                Today's Arrivals
              </h6>

              <p className="text-muted small mb-0">
                Managing guest stays and bookings
              </p>

            </div>

            <div className="card-body p-4">
              <BookingTable />
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default Index;